import { HttpService } from '@Services/api/httpService';
import { useBoundStore } from '@Store/useBoundStore';
import { handleLoginResponse } from '@Hooks/api/useLogin';
import { showResumeApplicationModal } from '@Hooks/modalManager/showResumeApplicationModal.tsx';
import type { LoginResponse } from '@Hooks/api/useLogin';

/**
 * Resumes an onboarding session that is authenticated by the HttpOnly `onboardingToken` cookie,
 * e.g. the webapp redirect to `?id=<customerId>` after a mid-onboarding web login (LC-31318), where
 * the session is no longer handed over as a URL token.
 *
 * This mirrors the URL-token direct-login flow (`handleDirectLoginFlowInitialData`) but assumes the
 * session is already authenticated by the cookie: it calls the same token-based login endpoint,
 * which returns the full onboarding state (onboardingStatus, lastStepData, customerData), and runs
 * it through the shared login handler. That rehydrates the customer store, marks the session active
 * and picks up where the user left off. A 401 is handled globally (session cleared, back to WELCOME).
 */
export const handleCookieResumeFlow = async (customerId: string): Promise<void> => {
  // Acts as the loginToken signal for the initial buildNavigationStack so it resumes the flow.
  useBoundStore.getState().onboardingSlice.setCookieSessionResume(true);

  const response = await HttpService.post(`/v3/secureonboarding/user/${customerId}/login`);
  const loginResponse = response.data as LoginResponse;

  await handleLoginResponse({
    loginResponse,
    onResume: () => showResumeApplicationModal(),
  });
};
